import { useCallback, useMemo } from 'react'
import { useUser, useIsUserC360 } from '@/features/account/services/User/hooks'
import {
  shoppingStoresQueryOptions,
  useQueryShoppingStores,
} from '@/features/shop/services/ShoppingStore/queries'
import { useQueryClient } from '@tanstack/react-query'
import { MembershipPlanType } from '@/features/account/services/MembershipPlan/types'
import { type ShoppingStoreDetails } from '@/features/shop/services/ShoppingStore/types'
import { type User } from '@/features/account/services/User/types'
import { getUser } from '@/features/account/services/User/utils'

const isStoreEligibleForZeroMarkups = (
  stores: ShoppingStoreDetails[] | undefined,
  storeId: User['store_id'] | undefined,
) =>
  stores?.some(
    ({ id, markups_type }) =>
      id === storeId &&
      (markups_type?.zero_markups === 'all' || markups_type?.zero_markups === 'exclusions'),
  )

export const useIsEligibleForC360NoMarkup = () => {
  const { store_id } = useUser() ?? {}
  const { data } = useQueryShoppingStores()
  const isCircle360 = useIsUserC360()

  return useMemo(() => {
    return Boolean(isCircle360 && isStoreEligibleForZeroMarkups(data?.delivery_stores, store_id))
  }, [data?.delivery_stores, isCircle360, store_id])
}

export const useGetIsEligibleForC360NoMarkup = () => {
  const queryClient = useQueryClient()

  return useCallback(() => {
    const { store_id, default_shopping_address_id, subscription } = getUser(queryClient) ?? {}
    if (subscription?.type !== MembershipPlanType.CIRCLE_360) return false

    const stores = queryClient.getQueryData(
      shoppingStoresQueryOptions({ address_id: default_shopping_address_id }).queryKey,
    )
    return isStoreEligibleForZeroMarkups(stores?.delivery_stores, store_id)
  }, [queryClient])
}
